import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  CaseDetailsWrapper,
  CaseDetailsHeader,
  CaseDetailsContent,
  LoadingState,
  CaseDetailsTitle,
  CaseInfo,
  InfoField,
  InfoLabel,
  InfoValue,
  SectionTitle,
  DocumentsSection,
  FinalStatementSection,
  FinalStatementLabel,
  FinalStatementText,
  FinalReportLink,
  NotesSection,
  NoteEntry,
  NoteEntryHead,
  NoteEntrySection,
  NoteEntryText,
  NoteEntryMeta,
  FinalReportInfo,
  FinalReportName,
  FinalReportMeta,
  EmptyState,
  AliasCode,
  CopyButton,
  CaseIdContainer,
  UploadSection,
  DropZone,
  DropZoneContent,
  UploadButton,
  InfoText,
  SemaforoIndicator,
  BrandingSection,
  BrandingCheckboxRow,
  BrandingPanel,
} from "./Styles";
import { IoIosArrowRoundBack } from "react-icons/io";
import { FiUpload, FiFile, FiFileText, FiCopy, FiFolder, FiCreditCard, FiLoader, FiArchive, FiRefreshCw, FiTrendingUp, FiCheckCircle, FiMessageSquare } from "react-icons/fi";
import { StatusBadge } from "../CasesList/Styles";
import { useDueDiligence } from "../../../Common/Hooks/useDueDiligence";
import { useDueDiligencePayment } from "../../../Common/Hooks/useDueDiligence/useDueDiligencePayment";
import { CreditDiscount } from "../Components/CreditDiscount";
import { useDueDiligenceFeedbackGate } from "../../../Common/Hooks/useDueDiligenceFeedbackGate";
import { useCanonicalCaseUrl } from "../../../Common/Hooks/useCanonicalCaseUrl";
import { useDueDiligenceReportNotes } from "../../../Common/Hooks/useDueDiligenceReportNotes";
import { sectionFullLabel } from "@dd-report-components/report/Sections";
import { useApi } from "../../../Common/Hooks/useApi";
import { useHttpMethods } from "../../../Common/Hooks/useApi/HttpMethods";
import { PdfSettingsForm } from "../../../Components/Sidebar/PdfSettingsModal/PdfSettingsForm";
import { DueDiligenceCase, DueDiligenceDocument, DocumentType as DocumentTypeEnum, DueDiligenceCaseStatuses, getDueDiligenceCaseStatusLabel } from "../../../Common/Types-DTOs/DueDiligence";
import { isErr } from "../../../Common/Utils/Result";
import { eventBus } from "../../../Common/EventBus";
import { Notification } from "../../../Components/Notifications";
import { Config } from "../../../Config";
import { DocumentsTable } from "../../../Components/DocumentsTable";
import { DocumentViewer } from "../../../Components/DocumentViewer";
import { formatAbbreviatedDate, formatShortDate } from "../../../Common/Utils/dateUtils";
import { UnarchiveConfirmModal } from "../Components/UnarchiveConfirmModal";
import { UpsellModal } from "../Components/UpsellModal";
import { DueDiligenceCreditCosts } from "../NewCase/PaymentSection";
import { Button } from "../../../Components/Button";
import { FeedbackGateOverlay } from "../../../Components/DueDiligenceFeedback/FeedbackGateOverlay";
import { ManualFeedbackButton, SubmittedBadge } from "../../../Components/DueDiligenceFeedback/Styles";
import { ProcessingChecklist } from "../../../Components/ProcessingMessage";
import { DueDiligenceTaskStatus } from "../../../Common/Hooks/useApi/Schemas";
import { TRAFFIC_LIGHT } from "@dd-report-components";

export const CaseDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { caseAlias } = useParams<{ caseAlias: string }>();
  const { getCase, updateCase, listDocuments, getDocumentSignedUrl, getDueDiligencePricing } = useDueDiligence();
  const { uploadDueDiligenceDocumentEndpoint } = useApi();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [caseData, setCaseData] = useState<DueDiligenceCase | null>(null);
  const [tasks, setTasks] = useState<DueDiligenceTaskStatus[]>([]);
  const [documents, setDocuments] = useState<DueDiligenceDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<DueDiligenceDocument | null>(null);
  const [selectedDocumentUrl, setSelectedDocumentUrl] = useState<string | null>(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const casePromiseRef = useRef<Promise<any> | null>(null);
  const docsPromiseRef = useRef<Promise<any> | null>(null);
  const [showUnarchiveConfirmModal, setShowUnarchiveConfirmModal] = useState(false);
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const [pricing, setPricing] = useState<{ basic: number | null; professional: number | null; premium: number | null } | null>(null);
  const [creditCosts, setCreditCosts] = useState<DueDiligenceCreditCosts | null>(null);
  const [customBrandingEnabled, setCustomBrandingEnabled] = useState(false);
  const { get: httpGet, del: httpDel } = useHttpMethods();
  const {
    feedbackStatus,
    feedbackConfig,
    isFeedbackModalOpen,
    isSubmittingFeedback,
    openFeedbackModal,
    closeFeedbackModal,
    handleSubmitFeedback,
    handleDismissFeedbackPrompt,
  } = useDueDiligenceFeedbackGate(caseAlias, caseData?.status === DueDiligenceCaseStatuses.completed);

  useCanonicalCaseUrl(caseAlias, caseData?.alias);

  const { notesBySection } = useDueDiligenceReportNotes(caseAlias);

  // MIKE-329: the notes the client left on the report, read-only here. The
  // section's own title lives in the report manifest, which this page never
  // loads; the group vocabulary is static and says enough ("Sección 6 ·
  // Urbanístico") to find your way back.
  const reportNotes = useMemo(
    () =>
      [...notesBySection.entries()]
        .sort(([a], [b]) => a - b)
        .flatMap(([, sectionNotes]) => sectionNotes),
    [notesBySection],
  );

  const MAX_FILES = Config.MAX_UPLOAD_FILES;
  const MAX_FILE_SIZE_MB = Config.MAX_UPLOAD_SIZE_MB; // 50MB per file
  const MAX_TOTAL_SIZE_MB = MAX_FILES * MAX_FILE_SIZE_MB; // 250MB total (5 files * 50MB)
  const MAX_TOTAL_SIZE_BYTES = MAX_TOTAL_SIZE_MB * 1024 * 1024;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

  const { isPayingNow, handlePayNow } = useDueDiligencePayment(caseAlias, caseData?.tier);

  const handleOpenUpsellModal = () => {
    setShowUpsellModal(true);
    if (!pricing) {
      getDueDiligencePricing().then((result) => {
        if (!isErr(result)) {
          setPricing(result.value.pricing);
          setCreditCosts(result.value.creditCosts);
        }
      });
    }
  };

  useEffect(() => {
    if (!caseAlias) return;
    httpGet<{ settings: { hasCustomBranding: boolean } }>(
      `/due-diligence/cases/${caseAlias}/pdf-settings`
    ).then((result) => {
      if ("value" in result && result.value) {
        setCustomBrandingEnabled(result.value.settings.hasCustomBranding);
      }
    });
  }, [caseAlias]);

  const handleToggleCustomBranding = async (enabled: boolean) => {
    setCustomBrandingEnabled(enabled);
    if (!enabled && caseAlias) {
      await httpDel(`/due-diligence/cases/${caseAlias}/pdf-settings`);
    }
  };

  useEffect(() => {
    // Scroll to upload section if hash is present
    if (window.location.hash === '#upload-section') {
      setTimeout(() => {
        const element = document.getElementById('upload-section');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
    }
  }, []);

  useEffect(() => {
    const loadCase = async () => {
      if (!caseAlias) {
        navigate("/due-diligence");
        return;
      }

      setIsLoading(true);
      try {
        if (!casePromiseRef.current) {
          casePromiseRef.current = getCase(caseAlias);
        }
        const result = await casePromiseRef.current;
        casePromiseRef.current = null;

        if (isErr(result)) {
          eventBus.emit(
            "NOTIFICATION",
            Notification.buildNotification(
              "error",
              t("dueDiligence.caseDetails.notifications.loadCaseFailed")
            )
          );
          console.error("Error loading case:", result.error);
          navigate("/due-diligence");
          return;
        }

        setCaseData(result.value.case);
        setTasks(result.value.tasks ?? []);

        // Load documents
        loadDocuments(caseAlias);
      } catch (error) {
        console.error("Error loading case:", error);
        eventBus.emit(
          "NOTIFICATION",
          Notification.buildNotification(
            "error",
            t("dueDiligence.caseDetails.notifications.loadCaseFailed")
          )
        );
        navigate("/due-diligence");
      } finally {
        setIsLoading(false);
      }
    };

    const loadDocuments = async (caseAlias: string) => {
      setIsLoadingDocuments(true);
      try {
        if (!docsPromiseRef.current) {
          docsPromiseRef.current = listDocuments(caseAlias);
        }
        const result = await docsPromiseRef.current;
        docsPromiseRef.current = null;

        if (isErr(result)) {
          console.error("Error loading documents:", result.error);
          setDocuments([]);
          return;
        }

        setDocuments(result.value.documents);
      } catch (error) {
        console.error("Error loading documents:", error);
        setDocuments([]);
      } finally {
        setIsLoadingDocuments(false);
      }
    };

    loadCase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseAlias, t]);

  useEffect(() => {
    // Keep the checklist live through both processing phases: unpaid
    // (pending_payment) and paid-but-still-processing (anything except
    // pending_payment/completed). Once completed there's nothing left to poll.
    const isProcessing = caseData?.status && caseData.status !== 'completed';
    if (!isProcessing || !caseAlias) return;

    const interval = setInterval(async () => {
      const result = await getCase(caseAlias);
      if (!isErr(result)) {
        setCaseData(result.value.case);
        setTasks(result.value.tasks ?? []);
      }
    }, 30000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseData?.status, caseAlias]);

  const TIER_LABELS: Record<string, string> = {
    basic: t("dueDiligence.caseDetails.tierLabels.basic"),
    professional: t("dueDiligence.caseDetails.tierLabels.professional"),
    premium: t("dueDiligence.caseDetails.tierLabels.premium"),
  };

  const OPERATION_TYPE_LABELS: Record<string, string> = {
    acquisition_investment: t("dueDiligence.caseDetails.operationTypeLabels.acquisitionInvestment"),
    divestment_sale: t("dueDiligence.caseDetails.operationTypeLabels.divestmentSale"),
    land_development: t("dueDiligence.caseDetails.operationTypeLabels.landDevelopment"),
    other: t("dueDiligence.caseDetails.operationTypeLabels.other"),
  };

  const handleFileUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const processFiles = async (files: FileList) => {
    if (!caseAlias) return;
    if (!files || files.length === 0) return;

    // Validate: maximum number of files
    if (files.length > MAX_FILES) {
      const errorMsg = t("dueDiligence.caseDetails.notifications.uploadTooManyFiles", { count: MAX_FILES });
      setUploadError(errorMsg);
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification(
          "error",
          errorMsg
        )
      );
      return;
    }

    // Validate: each file size (50MB per file)
    for (let i = 0; i < files.length; i++) {
      if (files[i].size > MAX_FILE_SIZE_BYTES) {
        const fileSizeMB = (files[i].size / 1024 / 1024).toFixed(2);
        const errorMsg = t("dueDiligence.caseDetails.notifications.fileTooLarge", {
          fileName: files[i].name,
          maxSizeMb: MAX_FILE_SIZE_MB,
          currentSizeMb: fileSizeMB,
        });
        setUploadError(errorMsg);
        eventBus.emit(
          "NOTIFICATION",
          Notification.buildNotification("error", errorMsg)
        );
        return;
      }
    }

    // Validate: total size less than MAX_TOTAL_SIZE_MB
    let totalSize = 0;
    for (let i = 0; i < files.length; i++) {
      totalSize += files[i].size;
    }
    if (totalSize > MAX_TOTAL_SIZE_BYTES) {
      const errorMsg = t("dueDiligence.caseDetails.notifications.totalSizeExceeded", {
        maxTotalMb: MAX_TOTAL_SIZE_MB,
        currentTotalMb: (totalSize / 1024 / 1024).toFixed(2),
      });
      setUploadError(errorMsg);
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification("error", errorMsg)
      );
      return;
    }

    setUploading(true);
    setUploadError(null);
    setUploadSuccess(false);
    try {
      // Upload all files in parallel
      const uploadPromises = Array.from(files).map(file =>
        uploadDueDiligenceDocumentEndpoint(caseAlias, file, 'additional_document' as DocumentTypeEnum)
      );

      const uploadResults = await Promise.all(uploadPromises);

      // Check for any errors
      for (let i = 0; i < uploadResults.length; i++) {
        const uploadResult = uploadResults[i];
        if (isErr(uploadResult)) {
          const errorMsg = t("dueDiligence.caseDetails.notifications.uploadFileFailed", {
            fileName: files[i].name,
            error: uploadResult.error.message || t("dueDiligence.caseDetails.notifications.unknownError"),
          });
          setUploadError(errorMsg);
          eventBus.emit(
            "NOTIFICATION",
            Notification.buildNotification("error", errorMsg)
          );
          setUploading(false);
          return;
        }
      }

      setUploadSuccess(true);
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification(
          "success",
          t("dueDiligence.caseDetails.notifications.uploadSuccess")
        )
      );

      // Add uploaded documents to the list immediately
      const newDocuments: DueDiligenceDocument[] = [];
      for (let i = 0; i < uploadResults.length; i++) {
        const result = uploadResults[i];
        if (!isErr(result) && result.value.document) {
          newDocuments.push(result.value.document);
        }
      }
      setDocuments(prevDocuments => [...prevDocuments, ...newDocuments]);

      // Hide success message
      setTimeout(() => {
        setUploadSuccess(false);
      }, 3000);
    } catch (err: any) {
      const errorMessage = err?.message || err?.error?.message || t("dueDiligence.caseDetails.notifications.unknownError");
      const errorMsg = t("dueDiligence.caseDetails.notifications.uploadFileFailedGeneric", { error: errorMessage });
      setUploadError(errorMsg);
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification("error", errorMsg)
      );
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      await processFiles(files);
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await processFiles(files);
    }
  };

  const handleDownloadDocument = async (documentId: string, _fileName: string) => {
    if (!caseAlias) return;

    const document = documents.find(doc => doc.id === documentId);
    if (!document) {
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification(
          "error",
          t("dueDiligence.caseDetails.notifications.documentNotFound")
        )
      );
      return;
    }

    setSelectedDocument(document);
    setSelectedDocumentUrl(null);
    setIsViewerOpen(true);

    try {
      const result = await getDocumentSignedUrl(caseAlias, document.id);

      if (isErr(result)) {
        eventBus.emit(
          "NOTIFICATION",
          Notification.buildNotification(
            "error",
            t("dueDiligence.caseDetails.notifications.getDocumentUrlFailed")
          )
        );
        console.error("Error getting signed URL:", result.error);
        return;
      }

      setSelectedDocumentUrl(result.value.signedUrl);
    } catch (error) {
      console.error("Error loading document:", error);
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification(
          "error",
          t("dueDiligence.caseDetails.notifications.loadDocumentFailed")
        )
      );
    }
  };

  const handleDocumentSelect = (document: DueDiligenceDocument) => {
    handleDownloadDocument(document.id, document.fileName);
  };

  const handleCloseViewer = () => {
    setIsViewerOpen(false);
    setSelectedDocument(null);
    setSelectedDocumentUrl(null);
  };

  const handleCopyCaseId = async () => {
    if (!caseData?.alias) return;

    try {
      await navigator.clipboard.writeText(caseData.alias);
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification(
          "success",
          t("dueDiligence.caseDetails.notifications.caseIdCopied")
        )
      );
    } catch (err) {
      console.error("Error copying to clipboard:", err);
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification(
          "error",
          t("dueDiligence.caseDetails.notifications.copyCaseIdFailed")
        )
      );
    }
  };

  const handleConfirmUnarchive = async () => {
    if (!caseData || !caseAlias) return;

    try {
      let newStatus: string;
      if (caseData.previousStatus) {
         newStatus = caseData.previousStatus;
      } else {
         newStatus = caseData.finalStatement ? "completed" : "under_review";
      }

      const result = await updateCase(caseAlias, { status: newStatus as any });

      if (isErr(result)) {
        eventBus.emit(
          "NOTIFICATION",
          Notification.buildNotification(
            "error",
            t("dueDiligence.caseDetails.notifications.restoreCaseFailed")
          )
        );
      } else {
        eventBus.emit(
          "NOTIFICATION",
          Notification.buildNotification(
            "success",
            t("dueDiligence.caseDetails.notifications.restoreCaseSuccess")
          )
        );
        setShowUnarchiveConfirmModal(false);
        // Refresh case data
        setIsLoading(true);
        const caseResult = await getCase(caseAlias);
        if (!isErr(caseResult)) {
            setCaseData(caseResult.value.case);
        }
        setIsLoading(false);
      }
    } catch (error) {
       console.error(error);
       eventBus.emit("NOTIFICATION", Notification.buildNotification("error", t("dueDiligence.caseDetails.notifications.unexpectedError")));
    }
  };

  if (isLoading) {
    return (
      <CaseDetailsWrapper>
        <CaseDetailsHeader onClick={() => navigate("/due-diligence")}>
          <IoIosArrowRoundBack />
          <span>{t("dueDiligence.caseDetails.header.backButton")}</span>
        </CaseDetailsHeader>
        <CaseDetailsContent>
          <LoadingState>
            <FiLoader size={32} />
            <p style={{ margin: 0 }}>{t("dueDiligence.caseDetails.loading")}</p>
          </LoadingState>
        </CaseDetailsContent>
      </CaseDetailsWrapper>
    );
  }

  if (!caseData) {
    return (
      <CaseDetailsWrapper>
        <CaseDetailsHeader onClick={() => navigate("/due-diligence")}>
          <IoIosArrowRoundBack />
          <span>{t("dueDiligence.caseDetails.header.backButton")}</span>
        </CaseDetailsHeader>
        <CaseDetailsContent>
          <p>{t("dueDiligence.caseDetails.notFound")}</p>
        </CaseDetailsContent>
      </CaseDetailsWrapper>
    );
  }

  return (
    <CaseDetailsWrapper>
      <CaseDetailsContent>
        <CaseDetailsHeader onClick={() => navigate("/due-diligence")}>
          <IoIosArrowRoundBack />
          <span>{t("dueDiligence.caseDetails.header.backButton")}</span>
        </CaseDetailsHeader>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2.5rem', flexWrap: 'wrap' }}>
          <CaseDetailsTitle style={{ margin: 0 }}>{caseData.name}</CaseDetailsTitle>
          <StatusBadge $status={caseData.status}>
            {getDueDiligenceCaseStatusLabel(caseData.status)}
          </StatusBadge>
          {caseData.trafficLightStatus && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <SemaforoIndicator status={caseData.trafficLightStatus} />
              <span style={{ fontSize: '0.875rem', color: '#666' }}>
                {t("dueDiligence.caseDetails.info.riskLabel", {
                  level:
                    caseData.trafficLightStatus === TRAFFIC_LIGHT.green ? t("dueDiligence.caseDetails.riskLevels.low") :
                      caseData.trafficLightStatus === TRAFFIC_LIGHT.yellow ? t("dueDiligence.caseDetails.riskLevels.medium") :
                        caseData.trafficLightStatus === TRAFFIC_LIGHT.red ? t("dueDiligence.caseDetails.riskLevels.high") : t("dueDiligence.caseDetails.riskLevels.unknown")
                })}
              </span>
            </div>
          )}
        </div>

        {caseData.status === "pending_payment" && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1rem',
            padding: '2rem',
            backgroundColor: '#F0FFFE',
            border: '1px solid #67D9D4',
            borderRadius: '0.5rem',
            marginBottom: '2rem',
            textAlign: 'center'
          }}>
             <h3 style={{ margin: 0, color: '#111827' }}>{t("dueDiligence.caseDetails.pendingPayment.title")}</h3>
             <p style={{ margin: 0, color: '#374151' }}>
               {t("dueDiligence.caseDetails.pendingPayment.message")}
             </p>

             <CreditDiscount caseId={caseData.id} onPaid={setCaseData} />

             <UploadButton
               onClick={handlePayNow}
               disabled={isPayingNow}
               style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: 'auto', backgroundColor: '#001d30', color: 'white', padding: '0.75rem 2rem', fontSize: '1.1rem', opacity: isPayingNow ? 0.7 : 1 }}
             >
                <FiCreditCard size={20} />
                {isPayingNow ? t("dueDiligence.caseDetails.pendingPayment.redirecting") : t("dueDiligence.caseDetails.pendingPayment.payNowButton")}
             </UploadButton>
          </div>
        )}

        <CaseInfo>
          <InfoField>
            <InfoLabel>{t("dueDiligence.caseDetails.info.caseIdLabel")}</InfoLabel>
            <InfoValue>
              <CaseIdContainer>
                <AliasCode>{caseData.alias}</AliasCode>
                <CopyButton
                  onClick={handleCopyCaseId}
                  title={t("dueDiligence.caseDetails.info.copyCaseIdTitle")}
                  aria-label={t("dueDiligence.caseDetails.info.copyCaseIdTitle")}
                >
                  <FiCopy />
                </CopyButton>
              </CaseIdContainer>
            </InfoValue>
          </InfoField>



          <InfoField>
            <InfoLabel>{t("dueDiligence.caseDetails.info.reportTypeLabel")}</InfoLabel>
            <InfoValue>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span>{caseData.tier ? TIER_LABELS[caseData.tier] ?? caseData.tier : "—"}</span>
                {caseData.status === 'pending_payment' && (
                  <span style={{ fontSize: '0.75rem', backgroundColor: '#FEF3C7', color: '#D97706', padding: '2px 8px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '4px', border: '1px solid #FCD34D' }}>
                    {t("dueDiligence.caseDetails.info.pendingPaymentBadge")}
                  </span>
                )}
                {caseData.status !== DueDiligenceCaseStatuses.pending_payment &&
                  caseData.status !== 'archived' &&
                  caseData.tier &&
                  caseData.tier !== 'premium' && (
                    <button
                      onClick={handleOpenUpsellModal}
                      style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', border: 'none', background: 'none', color: '#0D9488', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer', padding: 0 }}
                    >
                      <FiTrendingUp size={14} /> {t("dueDiligence.caseDetails.info.upgradeReportButton")}
                    </button>
                  )}
              </div>
            </InfoValue>
          </InfoField>

          {caseData.invoiceUrl && (
            <InfoField>
              <InfoLabel>{t("dueDiligence.caseDetails.info.invoiceLabel")}</InfoLabel>
              <InfoValue>
                <a
                  href={caseData.invoiceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#4F46E5', textDecoration: 'none', fontWeight: 500 }}
                >
                  <FiFile size={16} />
                  {t("dueDiligence.caseDetails.info.viewInvoiceLink")}
                </a>
              </InfoValue>
            </InfoField>
          )}

          <InfoField>
            <InfoLabel>{t("dueDiligence.caseDetails.info.createdAtLabel")}</InfoLabel>
            <InfoValue>{formatShortDate(caseData.createdAt)}</InfoValue>
          </InfoField>

          <InfoField>
            <InfoLabel>{t("dueDiligence.caseDetails.info.updatedAtLabel")}</InfoLabel>
            <InfoValue>{formatShortDate(caseData.updatedAt)}</InfoValue>
          </InfoField>

          {caseData.propertyAddress && (
            <InfoField>
              <InfoLabel>{t("dueDiligence.caseDetails.info.propertyAddressLabel")}</InfoLabel>
              <InfoValue>{caseData.propertyAddress}</InfoValue>
            </InfoField>
          )}

          {caseData.propertyType && (
            <InfoField>
              <InfoLabel>{t("dueDiligence.caseDetails.info.propertyTypeLabel")}</InfoLabel>
              <InfoValue>{caseData.propertyType}</InfoValue>
            </InfoField>
          )}

          {caseData.cadastralReference && (
            <InfoField>
              <InfoLabel>{t("dueDiligence.caseDetails.info.cadastralReferenceLabel")}</InfoLabel>
              <InfoValue>{caseData.cadastralReference}</InfoValue>
            </InfoField>
          )}

          {caseData.operationType && (
            <InfoField>
              <InfoLabel>{t("dueDiligence.caseDetails.info.operationTypeLabel")}</InfoLabel>
              <InfoValue>{OPERATION_TYPE_LABELS[caseData.operationType] ?? caseData.operationType}</InfoValue>
            </InfoField>
          )}

          {caseData.ownerName && (
            <InfoField>
              <InfoLabel>{t("dueDiligence.caseDetails.info.ownerNameLabel")}</InfoLabel>
              <InfoValue>{caseData.ownerName}</InfoValue>
            </InfoField>
          )}

          {caseData.ownerIdCard && (
            <InfoField>
              <InfoLabel>{t("dueDiligence.caseDetails.info.ownerIdCardLabel")}</InfoLabel>
              <InfoValue>{caseData.ownerIdCard}</InfoValue>
            </InfoField>
          )}

          {caseData.observations && (
            <InfoField>
              <InfoLabel>{t("dueDiligence.caseDetails.info.observationsLabel")}</InfoLabel>
              <InfoValue>{caseData.observations}</InfoValue>
            </InfoField>
          )}
        </CaseInfo>

        <BrandingSection>
          <SectionTitle>{t("dueDiligence.caseDetails.branding.sectionTitle")}</SectionTitle>
          <BrandingCheckboxRow>
            <input
              type="checkbox"
              checked={customBrandingEnabled}
              onChange={(e) => handleToggleCustomBranding(e.target.checked)}
            />
            {t("dueDiligence.caseDetails.branding.checkboxLabel")}
          </BrandingCheckboxRow>
          {customBrandingEnabled && caseAlias && (
            <BrandingPanel>
              <PdfSettingsForm basePath={`/due-diligence/cases/${caseAlias}/pdf-settings`} />
            </BrandingPanel>
          )}
        </BrandingSection>

        {(caseData.finalStatement || caseData.tier) && (
          <>
            <SectionTitle>{t("dueDiligence.caseDetails.conclusion.sectionTitle")}</SectionTitle>

            {caseData.finalStatement && (
              <FinalStatementSection>
                <FinalStatementLabel>{t("dueDiligence.caseDetails.conclusion.finalStatementLabel")}</FinalStatementLabel>
                <FinalStatementText>{caseData.finalStatement}</FinalStatementText>
              </FinalStatementSection>
            )}

            {caseData.tier && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '1280px' }}>
                {caseData.status === DueDiligenceCaseStatuses.completed ? (
                  <FinalReportLink
                    style={{ marginTop: 0 }}
                    onClick={() => navigate(`/due-diligence/case/${caseData.alias}/report`)}
                  >
                    <FiFileText />
                    <FinalReportInfo>
                      <FinalReportName>{t("dueDiligence.caseDetails.conclusion.finalReport.name")}</FinalReportName>
                      <FinalReportMeta>{t("dueDiligence.caseDetails.conclusion.finalReport.meta")}</FinalReportMeta>
                    </FinalReportInfo>
                  </FinalReportLink>
                ) : (
                  <FinalReportLink
                    style={{ marginTop: 0, display: 'block' }}
                    onClick={() => navigate(`/due-diligence/case/${caseData.alias}/report`)}
                  >
                    <ProcessingChecklist
                      title={
                        caseData.status === DueDiligenceCaseStatuses.pending_payment &&
                        tasks.length > 0 &&
                        tasks.every((t) => t.completed)
                          ? t("dueDiligence.caseDetails.conclusion.waitingPayment")
                          : t("dueDiligence.caseDetails.conclusion.generatingReport")
                      }
                      tasks={tasks}
                    />
                  </FinalReportLink>
                )}
              </div>
            )}
          </>
        )}

        {caseData.status === DueDiligenceCaseStatuses.completed && feedbackStatus && (
          <div style={{ marginTop: '0.5rem' }}>
            {feedbackStatus.alreadySubmitted ? (
              <SubmittedBadge>
                <FiCheckCircle size={16} />
                Feedback enviado
              </SubmittedBadge>
            ) : (
              // Always available here regardless of shouldAutoPrompt — the auto-popup
              // banner is a /report-only nudge (see FeedbackGateOverlay's showAutoPrompt),
              // it doesn't gate this page's manual entry point.
              <ManualFeedbackButton onClick={openFeedbackModal}>
                <FiMessageSquare size={16} />
                Danos tu opinión sobre este informe
              </ManualFeedbackButton>
            )}
          </div>
        )}

        <DocumentsSection>
          <SectionTitle>{t("dueDiligence.caseDetails.documents.sectionTitle")}</SectionTitle>

          {caseData.status !== DueDiligenceCaseStatuses.completed && caseData.status !== 'archived' ? (
            <UploadSection id="upload-section">
              {caseData.status === 'pending_payment' ? (
                 <div style={{
                   display: 'flex',
                   flexDirection: 'column',
                   alignItems: 'center',
                   gap: '1rem',
                   padding: '2rem',
                   backgroundColor: '#F3F4F6',
                   borderRadius: '0.5rem',
                   textAlign: 'center'
                 }}>
                    <FiFolder size={48} style={{ color: '#9CA3AF' }} />
                    <InfoText style={{ fontWeight: 500 }}>
                      {t("dueDiligence.caseDetails.documents.blocked.title")}
                    </InfoText>
                    <InfoText>
                      {t("dueDiligence.caseDetails.documents.blocked.message")}
                    </InfoText>
                 </div>
              ) : (
                <>
                  <InfoText style={{ marginBottom: 6, fontWeight: 500 }}>
                    {t("dueDiligence.caseDetails.documents.uploadPrompt.title")}
                  </InfoText>
                  <InfoText style={{ marginBottom: 6 }}>
                    {t("dueDiligence.caseDetails.documents.uploadPrompt.description")}
                  </InfoText>
                  <InfoText style={{ marginBottom: 24, fontSize: '0.875rem', color: '#666' }}>
                    {t("dueDiligence.caseDetails.documents.uploadPrompt.limitsHint", {
                      maxFiles: MAX_FILES,
                      maxFileSizeMb: MAX_FILE_SIZE_MB,
                      maxTotalSizeMb: MAX_TOTAL_SIZE_MB,
                    })}
                  </InfoText>
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    multiple
                    onChange={handleFileChange}
                    accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx"
                  />
                  <DropZone
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    $isDragging={isDragging}
                    $isUploading={uploading}
                    data-uploading={uploading ? "true" : "false"}
                  >
                    <DropZoneContent>
                      <FiFile size={48} />
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                        <strong>
                          {isDragging
                            ? t("dueDiligence.caseDetails.documents.dropZone.dragActive")
                            : uploading
                              ? t("dueDiligence.caseDetails.documents.dropZone.uploading")
                              : t("dueDiligence.caseDetails.documents.dropZone.idle")}
                        </strong>
                        <span>{t("dueDiligence.caseDetails.documents.dropZone.orSeparator")}</span>
                        <UploadButton onClick={handleFileUpload} disabled={uploading}>
                          <FiUpload />
                          {uploading ? t("dueDiligence.caseDetails.documents.dropZone.selectButtonLoading") : t("dueDiligence.caseDetails.documents.dropZone.selectButton")}
                        </UploadButton>
                      </div>
                    </DropZoneContent>
                  </DropZone>
                  {uploadError && <InfoText style={{ color: 'red', marginTop: 8 }}>{uploadError}</InfoText>}
                  {uploadSuccess && <InfoText style={{ color: 'green', marginTop: 8 }}>{t("dueDiligence.caseDetails.documents.uploadSuccessMessage")}</InfoText>}
                </>
              )}
            </UploadSection>
          ) : caseData.status === 'archived' ? (
            <UploadSection id="upload-section">
               <div style={{
                 display: 'flex',
                 flexDirection: 'column',
                 alignItems: 'center',
                 gap: '1rem',
                 padding: '2rem',
                 backgroundColor: '#F3F4F6',
                 borderRadius: '0.5rem',
                 textAlign: 'center'
               }}>
                   <FiArchive size={48} style={{ color: '#9CA3AF' }} />
                   <InfoText style={{ fontWeight: 500 }}>
                     {t("dueDiligence.caseDetails.documents.archived.title")}
                   </InfoText>
                   <InfoText style={{ marginBottom: '1rem' }}>
                     {t("dueDiligence.caseDetails.documents.archived.message")}
                   </InfoText>
                   <Button
                      onClick={() => setShowUnarchiveConfirmModal(true)}
                      style={{
                        backgroundColor: '#3B82F6',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem 1rem'
                      }}
                   >
                      <FiRefreshCw /> {t("dueDiligence.caseDetails.documents.archived.restoreButton")}
                   </Button>
                </div>
             </UploadSection>
           ) : null}


          {isLoadingDocuments ? (
            <EmptyState>{t("dueDiligence.caseDetails.documents.loading")}</EmptyState>
          ) : documents.length > 0 ? (
            <DocumentsTable
              documents={documents.filter(doc =>
                doc.documentType !== 'report_legal_fiscal_source' &&
                doc.visibleToClient !== false
              )}
              onDocumentSelect={handleDocumentSelect}
              selectedDocumentId={selectedDocument?.id}
            />
          ) : (
            <EmptyState>
              <FiFolder size={48} style={{ color: '#D1D5DB', marginBottom: '1rem' }} />
              <div style={{ fontSize: '0.875rem', color: '#6B7280' }}>
                {t("dueDiligence.caseDetails.documents.empty")}
              </div>
            </EmptyState>
          )}

          {isViewerOpen && (
            <DocumentViewer
              documentUrl={selectedDocumentUrl}
              fileName={selectedDocument?.fileName || null}
              mimeType={selectedDocument?.mimeType}
              onClose={handleCloseViewer}
            />
          )}
        </DocumentsSection>

        {reportNotes.length > 0 && (
          <NotesSection>
            <SectionTitle>{t("dueDiligence.caseDetails.notes.sectionTitle")}</SectionTitle>
            {reportNotes.map((note) => (
              <NoteEntry
                key={note.id}
                onClick={() => navigate(`/due-diligence/case/${caseData.alias}/report?notes=${note.sectionNumber}`)}
              >
                <NoteEntryHead>
                  <NoteEntrySection>{sectionFullLabel(note.sectionNumber)}</NoteEntrySection>
                  <NoteEntryMeta>
                    {note.authorName} · {formatAbbreviatedDate(note.createdAt)}
                  </NoteEntryMeta>
                </NoteEntryHead>
                <NoteEntryText>{note.content}</NoteEntryText>
              </NoteEntry>
            ))}
          </NotesSection>
        )}
        {/* Unarchive Confirm Modal */}
        {caseData && (
          <UnarchiveConfirmModal
               isOpen={showUnarchiveConfirmModal}
               onClose={() => setShowUnarchiveConfirmModal(false)}
               onConfirm={handleConfirmUnarchive}
               caseName={caseData.name}
               previousStatus={caseData.previousStatus}
               hasFinalStatement={!!caseData.finalStatement}
          />
        )}
        {caseData && caseData.tier && (
          <UpsellModal
            isOpen={showUpsellModal}
            onClose={() => setShowUpsellModal(false)}
            caseId={caseData.id}
            currentTier={caseData.tier}
            pricing={pricing}
            creditCosts={creditCosts}
            onUpgraded={setCaseData}
          />
        )}

        <FeedbackGateOverlay
          feedbackStatus={feedbackStatus}
          feedbackConfig={feedbackConfig}
          isFeedbackModalOpen={isFeedbackModalOpen}
          isSubmittingFeedback={isSubmittingFeedback}
          showAutoPrompt={false}
          onCloseModal={closeFeedbackModal}
          onSubmit={handleSubmitFeedback}
          onDismissAutoPrompt={handleDismissFeedbackPrompt}
        />
      </CaseDetailsContent>
    </CaseDetailsWrapper>
  );
};
